import React from 'react';
import LottieAnimation from './LottieAnimation';
import mirrorBallData from '../assets/lottie/mirror-ball-2.json';

/**
 * Loading spinner component
 * Design: Minimalist, high-contrast, rectangular animation
 * Uses Lottie mirror ball animation for fullScreen and large sizes
 */
export default function LoadingSpinner({
  size = 'md',
  label,
  fullScreen = false,
  className = '',
}) {
  const lottieSizes = {
    sm: 20,
    md: 40,
    lg: 60,
    xl: 80,
  };

  const lottieSize = fullScreen ? 240 : lottieSizes[size] || 40;

  const spinner = (
    <div className={`flex flex-col items-center justify-center gap-2 ${className}`}>
      <LottieAnimation
        animationData={mirrorBallData}
        width={lottieSize}
        height={lottieSize}
      />
      {label && (
        <p className="font-mono text-sm font-semibold uppercase tracking-wider text-black animate-pulse">
          {label}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
        {spinner}
      </div>
    );
  }

  return spinner;
}

/**
 * Inline loading dots
 */
export function LoadingDots({ className = '' }) {
  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <span className="w-2 h-2 bg-black animate-bounce" style={{ animationDelay: '0ms' }} />
      <span className="w-2 h-2 bg-black animate-bounce" style={{ animationDelay: '150ms' }} />
      <span className="w-2 h-2 bg-black animate-bounce" style={{ animationDelay: '300ms' }} />
    </div>
  );
}

/**
 * Skeleton loading placeholder
 */
export function Skeleton({
  width,
  height,
  className = '',
}) {
  return (
    <div
      className={`bg-gray-200 animate-pulse ${className}`}
      style={{ width, height }}
    />
  );
}

/**
 * Card skeleton for loading states
 */
export function CardSkeleton({ className = '' }) {
  return (
    <div className={`border-2 border-gray-200 p-4 ${className}`}>
      <div className="flex gap-4">
        <Skeleton className="w-24 h-24 flex-shrink-0" />
        <div className="flex-1 space-y-3">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
          <Skeleton className="h-2 w-full" />
        </div>
      </div>
    </div>
  );
}

/**
 * Row skeleton for tables/lists
 */
export function RowSkeleton({ className = '' }) {
  return (
    <div className={`border-2 border-gray-200 p-4 flex items-center gap-4 ${className}`}>
      <Skeleton className="w-12 h-12 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-1/4" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="w-20 h-8" />
        <Skeleton className="w-20 h-8" />
      </div>
    </div>
  );
}
